import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { useRef } from "react";
import {
  AppointmentControllerApi,
  GetAppointmentLightListDateRangeTypeEnum,
} from "../../../../services";
import {
  DGT_Pagination,
  getConfigObject,
  getPageIndexWhileSearch,
  getPageSize,
  getXtenantId,
  useBaseContent,
} from "../../../../utils/utils";
import { GridSortModel } from "@mui/x-data-grid";

interface UseAppointmentsV2Props {
  pagination: DGT_Pagination;
  gridSorting?: GridSortModel;
  startDate?: string;
  endDate?: string;
  providerGroupId?: string | null;
  appointmentStatus?: string[];
  appointmentMode?: string[];
  appStatusForPatient?: string[];
  appointmentType?: string[];
  providerUuid?: string[];
  patientUuid?: Array<string>;
  locationUuid?: Array<string>;
  // superBillGenerated?: boolean;
  billedStatus?: string;
  dateRangeType?: GetAppointmentLightListDateRangeTypeEnum;
  /**
   * ECRH-5881: invoice type the list is filtered by (SELF_PAY / OUT_OF_NETWORK
   * / PR), so the Create Invoice > Visit dropdown only offers visits that can
   * carry an invoice of that type.
   */
  invoiceType?: string;
  /**
   * Restricts the list to visits that can carry an insurance claim, so Self Pay
   * and Out of Network visits are never offered for claim creation.
   */
  claimEligible?: boolean;
  /** `null` leaves the `groupVisit` param off the request; omitted sends `false`. */
  groupVisit?: boolean | null;
  searchKey?: string;
  disabled?: boolean;
}

/**
 *
 * @author [Rajkumar Rathod]
 */
export function useAppointmentsV2(
  props: UseAppointmentsV2Props
): useBaseContent<any> {
  const prevPage = useRef<number>(props.pagination.page);

  const pageIndex = getPageIndexWhileSearch({
    pagination: props.pagination,
    searchKey: props.searchKey,
    prevPage: prevPage,
  });

  const pageSize = getPageSize(props.searchKey, props.pagination);
  const sortData = props.gridSorting?.[0];

  return useQuery({
    queryKey: ["appointment-list-v2", pageSize, pageIndex, props],
    enabled: !props?.disabled,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false,
    queryFn: async () => {
      // page?: number, size?: number, startDate?: string, endDate?: string,
      return new AppointmentControllerApi(getConfigObject())
        .getAppointmentLightList(
          pageIndex, //page
          pageSize, //size
          sortData?.field ?? undefined, // sortBy
          sortData?.sort ?? undefined, // sortDirection
          props.searchKey,
          props.startDate, //startDate
          props.endDate, //endDate
          props?.appointmentStatus,
          props?.appointmentMode,
          props?.appStatusForPatient,
          props?.appointmentType,
          props?.providerUuid,
          props?.patientUuid,
          props?.locationUuid,
          undefined, // paymentMode
          // remove superBillGenerated from here, to avoid billed status filter issue
          // props?.superBillGenerated,
          [props?.billedStatus ?? ""],
          props?.dateRangeType,
          props?.invoiceType,
          props?.claimEligible,
          props?.groupVisit === null ? undefined : (props?.groupVisit ?? false),
          props?.providerGroupId ?? getXtenantId() // xTENANTID
        )
        .then((it) => it?.data);
    },
  });
}
